import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { APIConnectionProfile } from '../model/screen/api-connection-profile-screen';
import { PageLoaderService } from './page-loader.service';
import { SandboxPageStateService } from './sandbox-page-state.service';
import { XmlToJsonParserService } from './xml-to-json-parser.service';
import { ConfigService } from './config.service';

export interface Response {
  status?: any;
  success?: any;
  error?: any;
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  apiURL: any;
  authClient: any;

  constructor(
    private httpClient: HttpClient,
    private sandboxPageStateService: SandboxPageStateService,
    private xmlToJsonParserService: XmlToJsonParserService,
    private pageLoaderService: PageLoaderService,
    private configService: ConfigService
  ) {
    this.apiURL = sandboxPageStateService.getConfig().ApiUrl;
  }

  async getXMLResponse(url: string, jsonRequestTemplate: string, inputParamsMap: Map<string, string>, options: any, xmlLogOverride?: string) {
    let isAPIRequestInitiated = false;
    let startTime = new Date().getTime();
    if (xmlLogOverride) {
      this.sandboxPageStateService.setXMLLogOverride(xmlLogOverride);
    } else {
      this.sandboxPageStateService.resetXMLLogOverride();
    }

    try {
      const apiConnectionProfile = this.sandboxPageStateService.getActiveAPIConnectionProfile();
      // console.log('Active API Connection Profile : ', apiConnectionProfile);

      if (apiConnectionProfile) {
        if (!apiConnectionProfile.tmcDetails.includeAggregator) {
          jsonRequestTemplate = this.deleteParticipants(jsonRequestTemplate);
        }

        if (apiConnectionProfile.corporateSenderDetails.corporateSender) {
          jsonRequestTemplate = this.addCorporateSender(jsonRequestTemplate, apiConnectionProfile);
        }

        if (apiConnectionProfile.corporateSenderDetails.directCorporate) {
          jsonRequestTemplate = this.addDirectCorporate(jsonRequestTemplate, apiConnectionProfile);
        }
      }
      const jsonRequest = this.getRequestTemplateWithInputParams(jsonRequestTemplate, inputParamsMap);

      const xmlRequest = this.xmlToJsonParserService.buildXMLFromJSON(JSON.parse(jsonRequest));
      const apiConnectionProfileURI = this.sandboxPageStateService
        .getConfig()
        .apiConnectionProfileURIs.find((apiConnectionProfileURI: { api_id: string }) => apiConnectionProfileURI.api_id === apiConnectionProfile.apiURI);
      let apiURI = '';
      if (apiConnectionProfileURI) {
        apiURI = apiConnectionProfileURI.api_uri + (apiConnectionProfile.apiURI === 'Custom' ? apiConnectionProfile.apiURICustom : '');
      }
      // console.log('API URL : ' + apiURI + url);

      isAPIRequestInitiated = true;
      const xmlResponseArrayBuffer = await this.getHTTPResponse(apiURI, url, xmlRequest, options);
      const xmlResponse = xmlResponseArrayBuffer as any;

      this.sandboxPageStateService.updateJsonRequestMap(url, jsonRequest);
      this.sandboxPageStateService.updateResponseMap(url, xmlResponse);
    } catch (error: any) {
      if (isAPIRequestInitiated) {
        // console.log('getXMLResponse : ', error);
        const errorMessage = error.message;

        const status: number = error.status;
        switch (status) {
          case 500:
            this.sandboxPageStateService.setErrorMessage('500 Internal Server Error');
            break;
          case 503:
            this.sandboxPageStateService.setErrorMessage('503 Service Unavailable');
            break;
          case 504:
            this.sandboxPageStateService.setErrorMessage('504 Gateway Timeout Error');
            break;
          default:
            this.sandboxPageStateService.setErrorMessage(errorMessage);
        }

        if (error.error) {
          this.sandboxPageStateService.updateResponseMapFromNonAPI(url, error.error);
        } else {
          delete error.headers;
          delete error.error;
          let errorXML = this.xmlToJsonParserService.buildXMLFromJSON(error);
          errorXML = errorXML.replace('<root>', '<Error>');
          errorXML = errorXML.replace('</root>', '</Error>');
          this.sandboxPageStateService.updateResponseMapFromNonAPI(url, errorXML);
        }
      } else {
        console.error(error);
      }
    }

    if (isAPIRequestInitiated) {
      let endTime = new Date().getTime();
      // console.log(url + ', ' + startTime + ', ' + endTime);

      let responseTime = Math.round((endTime - startTime) / 1000);
      if (url.includes('17.2') || url.includes('19.1')) {
        this.sandboxPageStateService.updatePerformanceLogsMap(url, responseTime + 's#0s#' + responseTime + 's');
      }
    }
  }
  async getJSONResponse(url: string, jsonRequestTemplate: string, inputParamsMap: Map<string, string>, options: any, xmlLogOverride?: string) {
    let isAPIRequestInitiated = false;
    let startTime = new Date().getTime();

    if (xmlLogOverride) {
      this.sandboxPageStateService.setXMLLogOverride(xmlLogOverride);
    } else {
      this.sandboxPageStateService.resetXMLLogOverride();
    }

    try {
      const apiConnectionProfile = this.sandboxPageStateService.getActiveAPIConnectionProfile();
      // console.log('Active API Connection Profile : ', apiConnectionProfile);

      if (apiConnectionProfile) {
        if (!apiConnectionProfile.tmcDetails.includeAggregator) {
          jsonRequestTemplate = this.deleteParticipants(jsonRequestTemplate, true);
        }

        if (apiConnectionProfile.corporateSenderDetails.corporateSender) {
          jsonRequestTemplate = this.addCorporateSender(jsonRequestTemplate, apiConnectionProfile, true);
        }

        if (apiConnectionProfile.corporateSenderDetails.directCorporate) {
          jsonRequestTemplate = this.addDirectCorporate(jsonRequestTemplate, apiConnectionProfile, true);
        }
      }

      const jsonRequest = this.getRequestTemplateWithInputParams(jsonRequestTemplate, inputParamsMap);
      const apiConnectionProfileURI = this.sandboxPageStateService
        .getConfig()
        .apiConnectionProfileURIs.find((apiConnectionProfileURI: { api_id: string }) => apiConnectionProfileURI.api_id === apiConnectionProfile.apiURI);
      let apiURI = '';
      if (apiConnectionProfileURI) {
        apiURI = apiConnectionProfileURI.api_uri + (apiConnectionProfile.apiURI === 'Custom' ? apiConnectionProfile.apiURICustom : '');
      }
      // console.log('API URL : ' + apiURI + url);

      isAPIRequestInitiated = true;
      const jsonResponseArrayBuffer = await this.getHTTPResponse(apiURI, url, jsonRequest, options);
      const jsonResponse = jsonResponseArrayBuffer as any;

      this.sandboxPageStateService.updateJsonRequestMap(url, jsonRequest);
      this.sandboxPageStateService.updateResponseMap(url, jsonResponse);
    } catch (error: any) {
      if (isAPIRequestInitiated) {
        // console.log('getXMLResponse : ', error);
        const errorMessage = error.message;

        const status: number = error.status;
        switch (status) {
          case 500:
            this.sandboxPageStateService.setErrorMessage('500 Internal Server Error');
            break;
          case 503:
            this.sandboxPageStateService.setErrorMessage('503 Service Unavailable');
            break;
          case 504:
            this.sandboxPageStateService.setErrorMessage('504 Gateway Timeout Error');
            break;
          default:
            this.sandboxPageStateService.setErrorMessage(errorMessage);
        }

        if (error.error) {
          this.sandboxPageStateService.updateResponseMapFromNonAPI(url, error.error);
        } else {
          delete error.headers;
          delete error.error;
          let errorXML = this.xmlToJsonParserService.buildXMLFromJSON(error);
          errorXML = errorXML.replace('<root>', '<Error>');
          errorXML = errorXML.replace('</root>', '</Error>');
          this.sandboxPageStateService.updateResponseMapFromNonAPI(url, errorXML);
        }
      } else {
        console.error(error);
      }
    }

    if (isAPIRequestInitiated) {
      let endTime = new Date().getTime();
      // console.log(url + ', ' + startTime + ', ' + endTime);

      let responseTime = Math.round((endTime - startTime) / 1000);
      if (url.includes('17.2') || url.includes('19.1')) {
        this.sandboxPageStateService.updatePerformanceLogsMap(url, responseTime + 's#0s#' + responseTime + 's');
      }
    }
  }

  deleteParticipants(jsonRequestTemplate: string, ocn?: boolean): string {
    const jsonRequestObject = JSON.parse(jsonRequestTemplate);
    if (ocn) {
      delete jsonRequestObject['party']['participants'];
    } else {
      let sandboxRequestEnvelop = jsonRequestObject['S:Envelope'];
      let sandboxRequestBody = sandboxRequestEnvelop['S:Body'];
      const type: string = Object.keys(sandboxRequestBody).at(0) || '';
      delete sandboxRequestBody[type]['Party']['Participants'];
    }

    jsonRequestTemplate = JSON.stringify(jsonRequestObject);

    return jsonRequestTemplate;
  }

  addCorporateSender(jsonRequestTemplate: string, apiConnectionProfile: APIConnectionProfile, ocn?: boolean): string {
    const recipientTemplate = {
      $: {
        'xsi:nil': 'true'
      }
    };

    const corporateSenderTemplate = {
      CorporateSender: {
        ID: apiConnectionProfile.corporateID
      }
    };

    const travelAgencyParticipant = {
      $: {
        SequenceNumber: '1'
      },
      Type: '##TravelAgencySender##Type##',
      Contacts: {
        Contact: {
          AddressContact: {
            Street: ['##AddressContact##Street1##', '##AddressContact##Street2##'],
            CityName: '##AddressContact##CityName##',
            PostalCode: '##AddressContact##PostalCode##',
            CountryCode: '##AddressContact##CountryCode##'
          },
          EmailContact: {
            Address: '##EmailContact##Address##'
          }
        }
      },
      IATA_Number: '##IATA_Number##',
      AgencyID: '##AgencyID##'
    };

    const jsonRequestObject = JSON.parse(jsonRequestTemplate);
    if (ocn) {
      jsonRequestObject['party']['sender'] = corporateSenderTemplate;
      jsonRequestObject['party']['participants']['participant']['travelAgencyParticipant'] = travelAgencyParticipant;
    } else {
      let sandboxRequestEnvelop = jsonRequestObject['S:Envelope'];
      let sandboxRequestBody = sandboxRequestEnvelop['S:Body'];
      const type: string = Object.keys(sandboxRequestBody).at(0) || '';
      sandboxRequestBody[type]['Party']['Sender'] = corporateSenderTemplate;

      if (!sandboxRequestBody[type]['Party']['Participants']) {
        sandboxRequestBody[type]['Party'] = {
          ...sandboxRequestBody[type]['Party'],
          Participants: {
            Participant: {
              TravelAgencyParticipant: {}
            }
          }
        };
      } else if (sandboxRequestBody[type]['Party']['Participants']['Participant']['AggregatorParticipant']) {
        sandboxRequestBody[type]['Party']['Participants']['Participant']['AggregatorParticipant'].$['SequenceNumber'] = '2';
      }

      delete sandboxRequestBody[type]['Party']['Recipient'];
      sandboxRequestBody[type]['Party']['Participants']['Participant']['TravelAgencyParticipant'] = travelAgencyParticipant;
      sandboxRequestBody[type]['Party']['Recipient'] = recipientTemplate;
    }

    jsonRequestTemplate = JSON.stringify(jsonRequestObject);
    return jsonRequestTemplate;
  }

  addDirectCorporate(jsonRequestTemplate: string, apiConnectionProfile: APIConnectionProfile, ocn?: boolean): string {
    const directCorporateTemplate = {
      CorporateSender: {
        ID: apiConnectionProfile.corporateID,
        IATA_Number: apiConnectionProfile.iata
      }
    };

    const jsonRequestObject = JSON.parse(jsonRequestTemplate);
    if (ocn) {
      jsonRequestObject['party']['sender'] = directCorporateTemplate;
    } else {
      let sandboxRequestEnvelop = jsonRequestObject['S:Envelope'];
      let sandboxRequestBody = sandboxRequestEnvelop['S:Body'];
      const type: string = Object.keys(sandboxRequestBody).at(0) || '';
      sandboxRequestBody[type]['Party']['Sender'] = directCorporateTemplate;
    }

    jsonRequestTemplate = JSON.stringify(jsonRequestObject);

    return jsonRequestTemplate;
  }

  getHTTPResponse(apiURI: string, url: string, xmlRequest: string, options: any) {
    switch (url) {
      // case this.sandboxPageStateService.FIND_ORDER_SCREEN_URL:
      // return this.httpClient.get('/assets/xml/response.xml', { responseType: 'text' }).toPromise();
      // case this.sandboxPageStateService.ORDER_CHANGE_URL:
      // return this.httpClient.get('/assets/json/find-order/response/OrderChangeSplit172V3RS7.xml', { responseType: 'text' }).toPromise();
      default:
        return this.httpClient.post(apiURI + url, xmlRequest, options).toPromise();
    }
  }

  getRequestTemplateWithInputParams(jsonRequest: string, inputParamsMap: Map<string, string>): string {
    inputParamsMap = this.overrideConnectionParams(inputParamsMap);
    inputParamsMap.forEach((value, key) => (jsonRequest = jsonRequest.replace(new RegExp(key, 'g'), value)));
    return jsonRequest;
  }

  overrideConnectionParams(inputParamsMap: Map<string, string>) {
    const apiConnectionProfile = this.sandboxPageStateService.getActiveAPIConnectionProfile();
    const config = this.sandboxPageStateService.getConfig();

    if (apiConnectionProfile) {
      inputParamsMap.set('##EmailContact##Address##', apiConnectionProfile.contactEMail);
      inputParamsMap.set('##IATA_Number##', apiConnectionProfile.iata);
      inputParamsMap.set('##AgencyID##', apiConnectionProfile.agencyName);
      inputParamsMap.set('##AggregatorParticipant##Name##', apiConnectionProfile.aggregatorName);
      inputParamsMap.set('##AggregatorParticipant##AggregatorID##', apiConnectionProfile.aggregatorID);

      if (config.contact) {
        inputParamsMap.set('##AddressContact##Street1##', config.contact.addressContact.street1);
        inputParamsMap.set('##AddressContact##Street2##', config.contact.addressContact.street2);
        inputParamsMap.set('##AddressContact##CityName##', config.contact.addressContact.cityName);
        inputParamsMap.set('##AddressContact##PostalCode##', config.contact.addressContact.postalCode);
        inputParamsMap.set('##AddressContact##CountryCode##', config.contact.addressContact.countryCode);
      }

      if (!inputParamsMap.has('##TravelAgencySender##Type##')) {
        inputParamsMap.set('##TravelAgencySender##Type##', 'offline');
      }
    }

    return inputParamsMap;
  }

  async sendPostRequest(data: any, endpoint: string): Promise<Response> {
    return this.sendPostRequestWithParams(data, endpoint);
  }

  async sendPostRequestWithParams(data: any, endpoint: string, queryParams?: any): Promise<Response> {
    const response: Response = {} as Response;
    this.pageLoaderService.addPageLoader(true);
    
    const httpOptions: any = {
      headers: new HttpHeaders(await this.getAuthHeaders())
    };
    
    if (queryParams) {
      httpOptions.params = queryParams;
    } 

    try {
      const responseBuffer = await this.httpClient.post(this.apiURL + endpoint, JSON.stringify(data), httpOptions).toPromise();
      response.success = responseBuffer as any;
    } catch (error: any) {
      response.error = error;
    }

    this.pageLoaderService.addPageLoader(false);
    return response;
  }

  async sendPutRequest(endpoint: string, data: any, contentType: string): Promise<Response> {
    let response: Response = {} as Response;
    this.pageLoaderService.addPageLoader(true);
    let httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': contentType
      })
    };

    try {
      const responseBuffer = await this.httpClient.put(endpoint, data, httpOptions).toPromise();
      response.success = responseBuffer as any;
    } catch (error: any) {
      response.error = error;
    }

    this.pageLoaderService.addPageLoader(false);
    return response;
  }

  async sendDeleteRequest(endpoint: string, queryParams?: any): Promise<Response> {
    let response: Response = {} as Response;
    this.pageLoaderService.addPageLoader(true);
    let httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      params: queryParams
    };

    try {
      const responseBuffer = await this.httpClient.delete(this.apiURL + endpoint, httpOptions).toPromise();
      response.success = responseBuffer as any;
    } catch (error: any) {
      response.error = error;
    }

    this.pageLoaderService.addPageLoader(false);
    return response;
  }

  private async getAuthHeaders(): Promise<any> {
    const headers: any = { 'Content-Type': 'application/json' };

    const tokens = this.getAuthTokens();
    this.addAuthTokensToHeaders(headers, tokens);
    
    return headers;
  }

  private getAuthTokens(): { accessToken: string | null; } {
    let accessToken = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
   
    if (accessToken?.startsWith('"')) accessToken = JSON.parse(accessToken);
    return { accessToken };
  }

  private addAuthTokensToHeaders(headers: any, tokens: { accessToken: string | null; }): void {
    if (tokens.accessToken) headers['Authorization'] = `Bearer ${tokens.accessToken}`;
  }

  async sendGetRequest(endpoint: string, queryParams?: any, responseType?: any): Promise<Response> {
    let response: Response = {} as Response;
    this.pageLoaderService.addPageLoader(true);
    
    const httpOptions = {
      headers: new HttpHeaders(await this.getAuthHeaders()),
      params: queryParams,
      responseType: responseType
    };
    
    try {
      const responseBuffer = await this.httpClient.get(this.apiURL + endpoint, httpOptions).toPromise();
      response.success = responseBuffer as any;
    } catch (error: any) {
      response.error = error;
    }
    
    this.pageLoaderService.addPageLoader(false);
    return response;
  }

  async sendDeleteRequestWithoutDomain(endpoint: string, queryParams?: any, responseType?: any): Promise<Response> {
    let response: Response = {} as Response;
    this.pageLoaderService.addPageLoader(true);
    let httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      params: queryParams,
      responseType: responseType
    };

    try {
      const responseBuffer = await this.httpClient.delete(endpoint, httpOptions).toPromise();
      response.success = responseBuffer as any;
    } catch (error: any) {
      response.error = error;
    }

    // console.log('sendGetRequest response : ', response);
    this.pageLoaderService.addPageLoader(false);
    return response;
  }

  getErrorMessage(error: any): string {
    let errorMessage: string = '';
    if (error.error instanceof ErrorEvent) {
      errorMessage = error.error.Message;
    } else {
      errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
    }
    return errorMessage;
  }
}
